import Checkbox from '@allesauto/core/checkbox';
import DropdownRange from '@allesauto/core/dropdownRange';
import { type Option } from '@allesauto/core/searchDropdown';
import VehicleIcon from '@allesauto/core/vehicleIcon';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { DropdownOpenHeight } from '@allesauto/utils/consts';
import { getSlug } from '@allesauto/utils/funcs';
import { Add16, Subtract16 } from '@carbon/icons-react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
export interface SelectedProps {
  minSeats: number;
  maxSeats: number;
  vehicleType: string[];
  doors: string[];
}
export interface VehicleTypeFilterProps {
  vehicleTypes: SchemaTypes.VehicleType[];
  doors: SchemaTypes.Door[];
  maxSeats: number;
  onChange?: (filterOptions: SelectedProps) => void;
  selected: SelectedProps;
}

// eslint-disable-next-line react/display-name
const VehicleTypeFilter: React.FC<
  React.PropsWithChildren<VehicleTypeFilterProps>
> = ({ vehicleTypes, doors, maxSeats, onChange, selected }) => {
  const [expanded, setExpanded] = useState(false);

  const seatsOptions: Option[] = [];
  for (let i = 2; i <= maxSeats; i++) {
    seatsOptions.push({ value: i, label: i });
  }

  const className = {
    container: clsx('h-full max-w-[360px]'),
    highlighted: clsx('gap-10px grid grid-cols-3 grid-flow-row'),
    extra: clsx(' ease-in-out gap-10px grid grid-cols-3'),
    expandText: clsx(
      'inline-block leading-5 mb-30px ml-[9px] mt-10px text-p1 text-white underline'
    ),
    iconStyle: clsx('inline-block text-white'),
    doorList: clsx('grid grid-cols-3 max-w-[300px] mb-30px'),
    seats: clsx('min-w-[230px]'),
    infoText: clsx('leading-5 mb-10px mt-10px text-p1 text-white'),
  };
  const {
    container,
    highlighted,
    extra,
    expandText,
    iconStyle,
    doorList,
    seats,
    infoText,
  } = className;
  const intl = useIntl();
  const maxVehiclesHighlighted = 6;
  const vehicleListRef = useRef<HTMLDivElement>(null);

  const onVehicleIconClick = useCallback(
    (name: string, added: boolean) => {
      let vehicleTypeList = [...(selected.vehicleType || [])];
      if (added) {
        vehicleTypeList.push(name);
      } else {
        vehicleTypeList = selected?.vehicleType.filter((e) => e !== name);
      }
      onChange &&
        onChange({
          ...selected,
          vehicleType: vehicleTypeList,
        });
    },
    [selected, onChange]
  );

  const onCheckboxClick = useCallback(
    (checked: boolean, id: SchemaTypes.DoorCount) => {
      let numberOfDoors = [...(selected.doors || [])];
      if (checked) {
        numberOfDoors.push(id);
      } else {
        numberOfDoors = numberOfDoors.filter((e) => e !== id);
      }
      onChange &&
        onChange({
          ...selected,
          doors: numberOfDoors,
        });
    },
    [selected, onChange]
  );

  const handleOnChangeRange = useCallback(
    (seats: any) => {
      // Needs proper type exposed by DropdownRange
      onChange &&
        onChange({
          ...selected,
          minSeats: seats.minValue?.value,
          maxSeats: seats.maxValue?.value,
        });
    },
    [selected, onChange]
  );

  const renderHighlighted = () =>
    vehicleTypes &&
    vehicleTypes
      .slice(0, maxVehiclesHighlighted)
      .map(
        (vehicleType: SchemaTypes.VehicleType) =>
          vehicleType && (
            <VehicleIcon
              key={vehicleType.name}
              name={vehicleType.name}
              onClick={onVehicleIconClick}
              displayName={vehicleType.displayName}
              isDefaultActive={
                selected?.vehicleType &&
                selected?.vehicleType.indexOf(vehicleType.name) !== -1
              }
            />
          )
      );

  const renderExtraVehicles = () =>
    vehicleTypes &&
    vehicleTypes
      .slice(maxVehiclesHighlighted, vehicleTypes.length)
      .map(
        (vehicleType: SchemaTypes.VehicleType) =>
          vehicleType && (
            <VehicleIcon
              key={vehicleType.name}
              name={vehicleType.name}
              onClick={onVehicleIconClick}
              displayName={vehicleType.displayName}
              isDefaultActive={
                selected?.vehicleType &&
                selected?.vehicleType.indexOf(vehicleType.name) !== -1
              }
            />
          )
      );

  const renderDoorsBox = () =>
    doors &&
    doors.map(
      (doorType: SchemaTypes.Door) =>
        doorType &&
        doorType.name && (
          <Checkbox
            id={`aa-filter--doors-${getSlug(doorType.name)}`}
            key={doorType.name}
            darkBg={true}
            label={doorType.displayName}
            onChange={(e) =>
              onCheckboxClick(e, doorType.name as SchemaTypes.DoorCount)
            }
            checked={selected && selected.doors.indexOf(doorType.name) > -1}
          />
        )
    );

  return (
    <div className={container}>
      <div className={highlighted}>{renderHighlighted()}</div>
      <Transition
        as={'div'}
        show={expanded}
        enter="transition-[max-height] duration-700 ease-out"
        enterFrom="!max-h-0 overflow-hidden"
        enterTo="overflow-hidden"
        leave="transition-[max-height] duration-700 ease-out"
        leaveFrom="overflow-hidden"
        leaveTo="!max-h-0 overflow-hidden"
        beforeEnter={() => {
          const elm = vehicleListRef.current;
          if (elm && elm.parentElement) {
            elm.parentElement.style.maxHeight = `${elm.clientHeight}px`;
          }
        }}
      >
        <div ref={vehicleListRef}>
          <div className={extra}>{renderExtraVehicles()}</div>
        </div>
      </Transition>
      {vehicleTypes && vehicleTypes.length > maxVehiclesHighlighted && (
        <div className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
          {expanded && <Subtract16 className={iconStyle} />}
          {!expanded && <Add16 className={iconStyle} />}
          <span className={expandText}>
            {expanded
              ? translate(intl, 'generic.showLess')
              : translate(intl, 'generic.showMore')}
          </span>
        </div>
      )}

      <div className={infoText}>
        {translate(intl, 'ui.VEHICLE_TYPE_LIST.numberOfDoors')}
      </div>
      <div className={doorList} data-tracking-id="aa-filter--doors">
        {renderDoorsBox()}
      </div>
      <div className={infoText}>
        {translate(intl, 'ui.VEHICLE_TYPE_LIST.numberOfSeats')}
      </div>
      <div className={seats} data-tracking-id="aa-filter--seats">
        <DropdownRange
          id="aa-filter--seats"
          context="relative-position"
          minOptions={[...seatsOptions]}
          maxOptions={[...seatsOptions.reverse()]}
          minLabel={translate(intl, 'ui.VEHICLE_TYPE_LIST.from')}
          maxLabel={translate(intl, 'ui.VEHICLE_TYPE_LIST.until')}
          label={translate(intl, 'ui.VEHICLE_TYPE_LIST.from')}
          darkBg={true}
          onChangeRange={handleOnChangeRange}
          rangeValues={{
            minValue: selected.minSeats
              ? { value: selected.minSeats, label: selected.minSeats }
              : null,
            maxValue: selected.maxSeats
              ? { value: selected.maxSeats, label: selected.maxSeats }
              : null,
          }}
          value={seatsOptions[0]}
          isClearable={false}
          isMulti={false}
          isSearchable={false}
          openHeight={DropdownOpenHeight.MEDIUM}
        />
      </div>
    </div>
  );
};

export default VehicleTypeFilter;
